import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle, Calendar, TrendingUp } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { MonthRangePicker } from '@/components/MonthRangePicker';
import { format, subMonths, startOfMonth, endOfMonth, eachMonthOfInterval } from 'date-fns';
import { businessToday } from '@/lib/format';

const formatCurrency = (amount: number) => new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);

const getDateFromTimestamp = (timestamp: string): string => timestamp.substring(0, 10);

export default function InsuranceClaimsAnalytics() {
    const [monthRange, setMonthRange] = useState({
        from: startOfMonth(subMonths(businessToday(), 11)),
        to: endOfMonth(businessToday()),
    });

    const fromDate = format(monthRange.from, 'yyyy-MM-dd');
    const toDate = format(monthRange.to, 'yyyy-MM-dd');

    const { data: summaryData, isLoading } = useQuery({
        queryKey: ['insurance-claims-analytics', fromDate, toDate],
        queryFn: async () => {
            const { data: claims, error } = await supabase
                .from('insurance_claims')
                .select('id, transactions(amount, transaction_at)');
            if (error) throw error;

            const filtered = (claims || []).filter(c => {
                const trans = c.transactions as { amount: number; transaction_at: string } | null;
                if (!trans?.transaction_at) return false;
                const transDate = getDateFromTimestamp(trans.transaction_at);
                return transDate >= fromDate && transDate <= toDate;
            });

            const totalAmount = filtered.reduce((sum, c) => {
                const trans = c.transactions as { amount: number; transaction_at: string } | null;
                return sum + (trans?.amount || 0);
            }, 0);
            const totalClaims = filtered.length;
            const avgAmount = totalClaims > 0 ? totalAmount / totalClaims : 0;

            // Monthly aggregation
            const months = eachMonthOfInterval({ start: monthRange.from, end: monthRange.to });
            const monthlyMap = new Map<string, number>();
            months.forEach(m => monthlyMap.set(format(m, 'yyyy-MM'), 0));

            filtered.forEach(c => {
                const trans = c.transactions as { amount: number; transaction_at: string } | null;
                if (!trans?.transaction_at) return;
                const monthKey = getDateFromTimestamp(trans.transaction_at).substring(0, 7);
                if (monthlyMap.has(monthKey)) {
                    monthlyMap.set(monthKey, (monthlyMap.get(monthKey) || 0) + (trans.amount || 0));
                }
            });

            const monthlyData = Array.from(monthlyMap.entries()).map(([month, amount]) => ({
                month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' }),
                amount,
            }));

            return { totalClaims, totalAmount, avgAmount, monthlyData };
        },
    });

    if (isLoading) {
        return (
            <div className="space-y-6">
                <div className="grid gap-4 md:grid-cols-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32" />)}</div>
                <Skeleton className="h-80" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-end">
                <MonthRangePicker monthRange={monthRange} onMonthRangeChange={setMonthRange} />
            </div>

            <div className="grid gap-4 md:grid-cols-3">
                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total Claimed</CardTitle>
                        <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-red-600">{formatCurrency(summaryData?.totalAmount || 0)}</div>
                        <p className="text-xs text-muted-foreground">{summaryData?.totalClaims || 0} claims</p>
                    </CardContent>
                </Card>

                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Avg Claim</CardTitle>
                        <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{formatCurrency(summaryData?.avgAmount || 0)}</div>
                        <p className="text-xs text-muted-foreground">Per claim</p>
                    </CardContent>
                </Card>

                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Period</CardTitle>
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{format(monthRange.from, 'MMM yy')} - {format(monthRange.to, 'MMM yy')}</div>
                        <p className="text-xs text-muted-foreground">Selected date range</p>
                    </CardContent>
                </Card>
            </div>

            <Card className="border border-border/40">
                <CardHeader>
                    <CardTitle>Monthly Insurance Claims</CardTitle>
                    <CardDescription>Claim amounts over time</CardDescription>
                </CardHeader>
                <CardContent>
                    <ResponsiveContainer width="100%" height={280}>
                        <AreaChart data={summaryData?.monthlyData}>
                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                            <XAxis dataKey="month" className="text-xs" />
                            <YAxis className="text-xs" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                            <Tooltip formatter={(value: number) => formatCurrency(value)} contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }} />
                            <Area type="monotone" dataKey="amount" stroke="hsl(var(--chart-5))" fill="hsl(var(--chart-5))" fillOpacity={0.6} name="Claims" />
                        </AreaChart>
                    </ResponsiveContainer>
                </CardContent>
            </Card>
        </div>
    );
}
